import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { FiX, FiArrowLeft, FiHash, FiImage, FiTrash2, FiUploadCloud, FiRefreshCw, FiPlusCircle, FiCamera } from 'react-icons/fi';
import { FiZap } from 'react-icons/fi'; // Import FiZap icon
import { toast } from 'react-toastify';
import AuthContext from '../context/AuthContext';
import axios from 'axios';
import { Html5Qrcode } from "html5-qrcode";

const ProductScannerModal = ({ isOpen, onClose, onScan }) => {
  const [isTorchOn, setIsTorchOn] = useState(false); // État pour la lampe de poche
  const html5QrCodeRef = useRef(null); // Référence pour l'instance Html5Qrcode

  useEffect(() => {
    let html5QrCode;
    if (isOpen) {
      html5QrCode = new Html5Qrcode("product-barcode-reader");
      html5QrCodeRef.current = html5QrCode; // Stocker l'instance
      const config = { fps: 10, qrbox: { width: 250, height: 150 } };

      html5QrCode.start(
        { facingMode: "environment" },
        config,
        (decodedText) => {
          onScan(decodedText);
          html5QrCode.stop().catch(() => {});
        }
      ).catch(err => {
        toast.error("Erreur d'accès à la caméra.");
        onClose();
      });
    }
    return () => {
      if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
        html5QrCodeRef.current.stop().catch(() => {});
        // S'assurer que la lampe de poche est éteinte à la fermeture
        if (isTorchOn) {
          html5QrCodeRef.current.setTorchOff().catch(() => {});
          setIsTorchOn(false);
        }
      }
    };
  }, [isOpen, onScan, onClose, isTorchOn]); // Ajouter isTorchOn aux dépendances

  // Fonction pour activer/désactiver la lampe de poche
  const toggleTorch = useCallback(async () => {
    if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
      try {
        if (isTorchOn) {
          await html5QrCodeRef.current.setTorchOff();
        } else {
          await html5QrCodeRef.current.setTorchOn();
        }
        setIsTorchOn(prev => !prev);
      } catch (err) {
        toast.error("Impossible d'activer/désactiver la lampe de poche.");
        console.error("Torch error:", err);
      }
    }
  }, [isTorchOn]);

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/90 z-[100] flex flex-col items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-sm overflow-hidden relative shadow-2xl">
        <button onClick={onClose} className="absolute top-4 right-4 z-10 bg-white/80 p-2 rounded-full text-gray-800"><FiX size={20}/></button>
        <div className="p-6 text-center border-b">
          <h3 className="text-xl font-bold text-gray-800">Scanner le code-barres</h3>
          {/* Bouton Lampe de poche */}
          <button
            onClick={toggleTorch}
            className={`mt-2 p-2 rounded-full transition-colors ${isTorchOn ? 'bg-yellow-100 text-yellow-600' : 'text-gray-400 hover:text-yellow-600 hover:bg-gray-100'}`}
            title="Activer/Désactiver la lampe de poche"
          >
            <FiZap size={20} />
          </button>
        </div>
        <div id="product-barcode-reader" className="w-full bg-black"></div>
        <div className="p-6 text-center text-sm text-gray-500">Placez le code du produit devant la caméra.</div>
      </div>
    </div>
  );
};

const ProductModal = ({ isOpen, onClose, onSave, product, categories = [] }) => {
  const { user } = useContext(AuthContext);
  const isPremium = user?.plan === 'premium';
  const isOwner = user?.role === 'owner';

  const getInitialFormData = () => ({
    name: '',
    barcode: '',
    category: '',
    purchasePrice: '',
    salePrice: '',
    quantity: '',
    lowStockThreshold: '',
    description: '',
    images: [],
    isAddingNewCategory: false,
    variants: [], // Ajout pour les variantes
    isService: false,
  });

  const [formData, setFormData] = useState(getInitialFormData());
  const [newImages, setNewImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (product) {
        // Mode édition
        const isExistingCategory = categories.includes(product.category);
        setFormData({
          name: product.name || '',
          barcode: product.barcode || '',
          category: product.category || '',
          purchasePrice: product.purchasePrice || '',
          salePrice: product.salePrice || '',
          quantity: product.quantity || 0,
          lowStockThreshold: product.lowStockThreshold || 5,
          description: product.description || '',
          images: product.images || [],
          isAddingNewCategory: !isExistingCategory && product.category,
          variants: product.variants || [],
          isService: product.isService || false,
        });
        setNewImages([]);
      } else {
        // Mode ajout : on réinitialise le formulaire
        setFormData(getInitialFormData());
        setNewImages([]);
      }
    }
  }, [product, isOpen, categories]);

  // Mettre à jour la quantité totale si les variantes changent
  useEffect(() => {
    if (formData.variants.length > 0) {
      const totalQuantity = formData.variants.reduce((sum, v) => sum + (parseInt(v.quantity, 10) || 0), 0);
      setFormData(prev => ({ ...prev, quantity: totalQuantity }));
    }
  }, [formData.variants]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleCategoryChange = (e) => {
    if (e.target.value === 'add_new') {
      setFormData(prev => ({ ...prev, isAddingNewCategory: true, category: '' }));
    } else {
      setFormData(prev => ({ ...prev, category: e.target.value, isAddingNewCategory: false }));
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const totalImages = formData.images.length + newImages.length + files.length;

    if (totalImages > 3) {
      toast.warn("Vous ne pouvez ajouter que 3 images maximum par produit.");
      return;
    }
    setNewImages(prev => [...prev, ...files]);
  };

  const removeExistingImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const removeNewImage = (index) => {
    setNewImages(prev => prev.filter((_, i) => i !== index));
  };

  const generateBarcode = () => {
    // Génère un code interne aléatoire à 12 chiffres (préfixe 200 pour usage interne)
    const randomPart = Math.floor(100000000 + Math.random() * 900000000);
    setFormData(prev => ({ ...prev, barcode: `200${randomPart}` }));
  };

  const handleVariantChange = (index, field, value) => {
    const newVariants = [...formData.variants];
    newVariants[index][field] = value;
    setFormData(prev => ({ ...prev, variants: newVariants }));
  };

  const addVariant = () => {
    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, { name: '', quantity: '' }]
    }));
  };

  const removeVariant = (index) => {
    setFormData(prev => ({ ...prev, variants: prev.variants.filter((_, i) => i !== index) }));
  };

  const uploadToCloudinary = async (file) => {
    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
    data.append("cloud_name", process.env.REACT_APP_CLOUDINARY_CLOUD_NAME);

    try {
      const res = await axios.post(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
        data
      );
      return res.data.secure_url;
    } catch (error) {
      console.error("Erreur upload Cloudinary", error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Le prix d'achat n'est obligatoire que pour les produits physiques
    if (!formData.name || !formData.salePrice || (!formData.isService && !formData.purchasePrice)) {
      toast.error("Veuillez remplir tous les champs obligatoires.");
      return;
    }

    setUploading(true);
    let uploadedUrls = [];

    try {
      // Upload des nouvelles images
      if (newImages.length > 0) {
        const uploadPromises = newImages.map(file => uploadToCloudinary(file));
        uploadedUrls = await Promise.all(uploadPromises);
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi des images. Veuillez vérifier votre connexion.");
      setUploading(false);
      return;
    }

    const productData = { ...formData };
    // Combiner les anciennes et nouvelles images
    productData.images = [...formData.images, ...uploadedUrls];

    // Convertir les prix et la quantité en nombres
    // Si pas de prix d'achat (service), on met 0 par défaut
    productData.purchasePrice = parseFloat(productData.purchasePrice) || 0;
    productData.salePrice = parseFloat(productData.salePrice);
    
    if (!productData.isService && productData.variants.length === 0) {
        productData.quantity = parseInt(productData.quantity, 10);
        if (isNaN(productData.quantity)) productData.quantity = 0;
        productData.lowStockThreshold = parseInt(productData.lowStockThreshold, 10);
        if (isNaN(productData.lowStockThreshold)) productData.lowStockThreshold = 5;
    }
    
    await onSave(productData, product?._id);
    setUploading(false);
  };

  const handleScanResult = (decodedText) => {
    setFormData(prev => ({ ...prev, barcode: decodedText }));
    setIsScannerOpen(false);
    toast.success("Code-barres récupéré !");
  };

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 z-40 flex justify-center items-center">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">{product ? 'Modifier le produit' : 'Ajouter un nouveau produit'}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
            <FiX size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="name" className="block text-xs font-bold text-gray-600 mb-1">Nom du produit</label>
              <input id="name" type="text" placeholder="Nom du produit" value={formData.name} onChange={handleChange} className="w-full p-2 border rounded" required disabled={!isOwner} autoFocus={!product} />
            </div>
            <div>
              <label htmlFor="barcode" className="block text-xs font-bold text-gray-600 mb-1">Code-barres</label>
              <div className="flex gap-1">
                <input id="barcode" type="text" placeholder="Scan ou Générer" value={formData.barcode} onChange={handleChange} className="w-full p-2 border rounded-l" disabled={!isOwner} />
                <button type="button" onClick={() => setIsScannerOpen(true)} className="bg-gray-100 border px-3 hover:bg-gray-200 text-gray-600" title="Scanner avec la caméra" disabled={!isOwner}>
                    <FiCamera />
                </button>
                <button type="button" onClick={generateBarcode} className="bg-gray-100 border border-l-0 rounded-r px-3 hover:bg-gray-200 text-gray-600" title="Générer un code aléatoire" disabled={!isOwner}>
                    <FiRefreshCw />
                </button>
              </div>
              <p className="text-[10px] text-gray-400 mt-1">Laissez vide pour les services</p>
            </div>
            <div>
              <label htmlFor="category" className="block text-xs font-bold text-gray-600 mb-1">Catégorie</label>
              {formData.isAddingNewCategory ? (
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, isAddingNewCategory: false, category: '' }))} className="p-2 bg-gray-200 rounded hover:bg-gray-300" title="Annuler" disabled={!isOwner}>
                    <FiArrowLeft />
                  </button>
                  <input id="category" type="text" placeholder="Nom de la nouvelle catégorie" value={formData.category} onChange={handleChange} className="w-full p-2 border rounded" autoFocus disabled={!isOwner} />
                </div>
              ) : (
                <select id="category" value={formData.category} onChange={handleCategoryChange} className="w-full p-2 border rounded bg-white" disabled={!isOwner}>
                  <option value="">-- Choisir une catégorie --</option>
                  {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  <option value="add_new" className="font-bold text-brand-blue">Ajouter une nouvelle catégorie...</option>
                </select>
              )}
            </div>
          </div>

          {/* Type de produit (Service vs Physique) */}
          <div className="mb-4">
            <label className="flex items-center space-x-2 cursor-pointer p-2 bg-blue-50 border border-blue-100 rounded-lg">
              <input type="checkbox" checked={formData.isService} onChange={(e) => setFormData(prev => ({ ...prev, isService: e.target.checked }))} className="form-checkbox h-5 w-5 text-brand-blue rounded" />
              <span className="text-sm font-medium text-blue-800">C'est un Service (Pas de gestion de stock requis)</span>
            </label>
          </div>

          {/* Description */}
          <div className="mb-4">
            <label htmlFor="description" className="block text-xs font-bold text-gray-600 mb-1">Description (visible sur la boutique)</label>
            <textarea 
              id="description" 
              rows="3" 
              placeholder="Description détaillée du produit..." 
              value={formData.description} 
              onChange={handleChange} 
              className="w-full p-2 border rounded" 
              disabled={!isOwner}
            />
          </div>

          {/* Gestion des Variantes */}
          {!formData.isService && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
              <label className="block text-sm font-bold text-gray-600 mb-2">Variantes (par Taille, Couleur, etc.)</label>
              {formData.variants.map((variant, index) => (
                <div key={index} className="flex items-center gap-2 mb-2">
                  <input 
                    type="text" 
                    placeholder="Nom (ex: S, Rouge)" 
                    value={variant.name} 
                    onChange={(e) => handleVariantChange(index, 'name', e.target.value)}
                    className="w-full p-2 border rounded"
                  />
                  <input 
                    type="number" 
                    placeholder="Qté" 
                    value={variant.quantity} 
                    onChange={(e) => handleVariantChange(index, 'quantity', e.target.value)}
                    className="w-32 p-2 border rounded"
                  />
                  <button type="button" onClick={() => removeVariant(index)} className="p-2 text-red-500 hover:bg-red-100 rounded-full">
                    <FiTrash2 />
                  </button>
                </div>
              ))}
              <button type="button" onClick={addVariant} className="text-sm font-semibold text-brand-blue hover:underline flex items-center mt-2">
                <FiPlusCircle className="mr-2" /> Ajouter une variante
              </button>
              {formData.variants.length > 0 && <p className="text-xs text-gray-500 mt-2">La quantité totale sera calculée automatiquement.</p>}
            </div>
          )}

          {/* Gestion des Images */}
          <div className="mb-6">
            <label className="block text-xs font-bold text-gray-600 mb-2">Images du produit (Max 3)</label>
            
            <div className="flex flex-wrap gap-4">
              {/* Images existantes */}
              {formData.images.map((url, index) => (
                <div key={`existing-${index}`} className="relative w-24 h-24 border rounded-lg overflow-hidden group">
                  <img src={url} alt={`Produit ${index}`} className="w-full h-full object-cover" />
                  {isOwner && (
                    <button type="button" onClick={() => removeExistingImage(index)} className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                      <FiTrash2 size={12} />
                    </button>
                  )}
                </div>
              ))}

              {/* Nouvelles images (prévisualisation) */}
              {newImages.map((file, index) => (
                <div key={`new-${index}`} className="relative w-24 h-24 border rounded-lg overflow-hidden group border-brand-blue border-dashed">
                  <img src={URL.createObjectURL(file)} alt="Aperçu" className="w-full h-full object-cover opacity-80" />
                  <button type="button" onClick={() => removeNewImage(index)} className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full">
                    <FiTrash2 size={12} />
                  </button>
                </div>
              ))}

              {/* Bouton d'ajout */}
              {(formData.images.length + newImages.length) < 3 && isOwner && (
                <label className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-brand-blue hover:bg-blue-50 transition-colors">
                  <FiUploadCloud className="text-gray-400 mb-1" size={24} />
                  <span className="text-xs text-gray-500">Ajouter</span>
                  <input type="file" accept="image/*" multiple onChange={handleImageChange} className="hidden" />
                </label>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {isOwner && (
              <div>
                <label htmlFor="purchasePrice" className="block text-xs font-bold text-gray-600 mb-1">{formData.isService ? "Coût (Optionnel)" : "Prix d'achat"}</label>
                <input id="purchasePrice" type="number" placeholder={formData.isService ? "0" : "Prix d'achat"} value={formData.purchasePrice} onChange={handleChange} className="w-full p-2 border rounded" required={!formData.isService} />
              </div>
            )}
            <div>
              <label htmlFor="salePrice" className="block text-xs font-bold text-gray-600 mb-1">Prix de vente</label>
              <input id="salePrice" type="number" placeholder="Prix de vente" value={formData.salePrice} onChange={handleChange} className="w-full p-2 border rounded" required disabled={!isOwner} />
            </div>
            {!formData.isService && (
              <>
              <div title={formData.variants.length > 0 ? "Calculé depuis les variantes" : ""}>
                <label htmlFor="quantity" className="block text-xs font-bold text-gray-600 mb-1">Quantité</label>
                <input id="quantity" type="number" placeholder="Quantité" value={formData.quantity} onChange={handleChange} className="w-full p-2 border rounded bg-gray-100" required disabled={!isOwner || formData.variants.length > 0} />
              </div>
              {isPremium && (
                <div>
                  <label htmlFor="lowStockThreshold" className="block text-xs font-bold text-gray-600 mb-1">Seuil d'alerte</label>
                  <input id="lowStockThreshold" type="number" placeholder="Seuil d'alerte" value={formData.lowStockThreshold} onChange={handleChange} className="w-full p-2 border rounded" title="Seuil d'alerte pour le stock faible (Premium)" disabled={!isOwner} />
                </div>
              )}
              </>
            )}
          </div>
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={onClose} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg">
              Annuler
            </button>
            <button type="submit" className="bg-brand-green hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center" disabled={!isOwner || uploading}>
              {uploading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
              {uploading ? 'Envoi...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
    <ProductScannerModal isOpen={isScannerOpen} onClose={() => setIsScannerOpen(false)} onScan={handleScanResult} />
    </>
  );
};

export default ProductModal;